import { API } from '../../api';
import { Spin, Table, Typography } from 'antd';
import { handleError } from '../../utils/util';
import React, { useEffect, useState } from 'react';

const { Title } = Typography;

export default function ExtractDiffTable(props) {
  const { baseVersion, compareVersion, resourceType } = props;
  const [diffRows, setDiffRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareDefinitions, setCompareDefinitions] = useState();
  const [baseDefinitions, setBaseDefinitions] = useState();

  const generateVersionMap = definitions => {
    const versionMap = {};

    for (const definition of definitions) {
      versionMap[definition.column_name] = definition;
    }

    return versionMap;
  };

  const getVersionDefinitions = async cleanupObj => {
    setCompareDefinitions(null);
    setBaseDefinitions(null);

    try {
      const compareResponse = await API.get(
        `/validate/extracts/definition?resourceType=${resourceType}&version=${compareVersion}`,
      );
      const baseResponse = await API.get(
        `/validate/extracts/definition?resourceType=${resourceType}&version=${baseVersion}`,
      );
      const compareVersionMap = generateVersionMap(compareResponse.data);
      const baseVersionMap = generateVersionMap(baseResponse.data);

      if (cleanupObj.isMounted) {
        setCompareDefinitions(compareVersionMap);
        setBaseDefinitions(baseVersionMap);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const checkForChanges = (compareDef, baseDef) => {
    const keysToCheck = ['required', 'nullable', 'allowed', 'description', 'type'];
    let changes = {};
    keysToCheck.forEach(key => {
      if (compareDef[key] !== baseDef[key]) {
        changes[key] = {
          from: `${compareVersion}: ${compareDef[key]}`,
          to: `${baseVersion}: ${baseDef[key]}`,
        };

        if (changes.changeList) {
          changes.changeList.push(key);
        } else {
          changes.changeList = [key];
        }
      }
    });

    if (Object.keys(changes).length > 0) {
      changes = { ...compareDef, ...changes, changed: true };
    }

    return changes;
  };

  const generateDiff = async cleanupObj => {
    if (!baseVersion || !compareVersion) {
      setLoading(false);
      return;
    }

    setDiffRows([]);
    await getVersionDefinitions(cleanupObj);
  };

  const findChanges = () => {
    // find additions
    const additions = [];
    Object.keys(compareDefinitions).forEach(key => {
      if (!baseDefinitions[key]) {
        additions.push({ ...compareDefinitions[key], addition: true });
      }
    });
    // find deletions
    const deletions = [];
    Object.keys(baseDefinitions).forEach(key => {
      if (!compareDefinitions[key]) {
        deletions.push({ ...baseDefinitions[key], deletion: true });
      }
    });
    // find changes
    const changes = [];
    Object.keys(compareDefinitions).forEach(key => {
      if (compareDefinitions[key] && baseDefinitions[key]) {
        const changedFields = checkForChanges(compareDefinitions[key], baseDefinitions[key]);

        if (Object.keys(changedFields).length > 0) {
          changes.push(changedFields);
        }
      }
    });

    // set the diff rows
    setDiffRows([...diffRows, ...additions, ...deletions, ...changes]);
  };

  useEffect(() => {
    setLoading(true);
    const cleanup = {
      isMounted: true,
    };

    generateDiff(cleanup);

    return () => {
      cleanup.isMounted = false;
    };
  }, [baseVersion, compareVersion]);

  useEffect(() => {
    if (compareDefinitions && baseDefinitions) {
      findChanges();
      setLoading(false);
    }
  }, [compareDefinitions, baseDefinitions]);

  /**
   * Determine table cell style
   */
  const getCellStyle = (record, col) => {
    let background = 'white';
    let textDecoration = 'none';

    if (record.addition) {
      background = '#BFFFBB';
    } else if (record.deletion) {
      background = '#FFBBBB';
      textDecoration = 'line-through';
    } else if (record.changed && record.changeList.includes(col)) {
      background = '#FFFFBB';
    }

    return {
      textDecoration,
      background,
    };
  };

  const generateRender = (value, record, col) => {
    let display;

    if (value.from && value.to) {
      display = (
        <>
          <div>{value.from.toString()}</div>
          <div>{value.to.toString()}</div>
        </>
      );
    } else {
      display = <div>{value.toString()}</div>;
    }

    return {
      props: {
        style: getCellStyle(record, col),
        className: 'extract-diff-table-cell',
      },
      children: <div>{display}</div>,
    };
  };

  const tableColumns = [
    {
      title: <p>Column Name</p>,
      dataIndex: 'column_name',
      key: 'column_name',
      width: '20%',
      render(value, record) {
        return generateRender(value, record, 'column_name');
      },
    },
    {
      title: <p>Required</p>,
      dataIndex: 'required',
      key: 'required',
      width: '7.5%',
      render(value, record) {
        return generateRender(value, record, 'required');
      },
    },
    {
      title: <p>Nullable</p>,
      dataIndex: 'nullable',
      key: 'nullable',
      width: '7.5%',
      render(value, record) {
        return generateRender(value, record, 'nullable');
      },
    },
    {
      title: <p>Allowed Values</p>,
      dataIndex: 'allowed',
      key: 'allowed',
      width: '20',
      render(value, record) {
        return generateRender(value, record, 'allowed');
      },
    },
    {
      title: <p>Description</p>,
      dataIndex: 'description',
      key: 'description',
      width: '35%',
      render(value, record) {
        return generateRender(value, record, 'description');
      },
    },
    {
      title: <p>Type</p>,
      dataIndex: 'type',
      key: 'type',
      width: '10%',
      render(value, record) {
        return generateRender(value, record, 'type');
      },
    },
  ];

  return (
    <>
      {loading && <Spin />}
      {!loading && diffRows.length > 0 && (
        <>
          <Title level={4} style={{ marginTop: '20px' }}>
            Additions, deletions, and changes from {baseVersion}:
          </Title>
          <Table
            columns={tableColumns}
            rowKey="id"
            scroll={{ x: '100%', y: '100%' }}
            dataSource={diffRows}
            pagination={false}
          />
        </>
      )}
      {!loading && baseVersion && compareVersion && diffRows.length === 0 && (
        <Title level={5} style={{ marginTop: '20px' }}>
          No changes detected between the selected versions.
        </Title>
      )}
    </>
  );
}
